import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from '@busbook/ui';
import { useCustomer } from '../lib/customer-session.js';
import { nextParam } from '../lib/next-path.js';
import { useT } from '../i18n/index.js';

/**
 * The screens that need an account (DECISIONS D68).
 *
 * Searching is public: anyone may look at routes, times and prices without being asked who
 * they are. Taking a seat is not — a booking carries a passenger's name, becomes a ticket,
 * and has to belong to somebody who can find it again.
 *
 * This is the courtesy, not the rule. The API refuses an unauthenticated booking on its own
 * (routes.public.ts); what this adds is sending the passenger to sign in BEFORE they pick
 * seats and type names, rather than after.
 */
export function RequireCustomer({ children }: { children: ReactNode }) {
  const { user, loading } = useCustomer();
  const location = useLocation();
  const t = useT();

  // Waiting matters: the session is restored asynchronously from a stored refresh token, and
  // redirecting during that window would throw an already-signed-in passenger out of their
  // own booking every time they reloaded the page.
  if (loading) {
    return (
      <div className="grid place-items-center py-20">
        <Spinner label={t('m.loading')} />
      </div>
    );
  }

  if (!user) {
    // `replace`, so the back button returns to the trip list rather than bouncing through
    // this guard again. `next` carries the exact trip so signing in resumes it.
    return (
      <Navigate
        to={`/auth/login?next=${nextParam(location.pathname, location.search)}`}
        replace
      />
    );
  }

  return <>{children}</>;
}
